import { FC, useEffect, useRef, useState } from 'react';
import Api from "../../../../api";
import FullscreenModal from "components/FullscreenModal";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {SimpleModal} from "components/Modal";
import FixedLoader from "components/FixedLoader/FixedLoader";
import LeftHandedModal from "../../../landing/SignUp2_Talent/components/LeftHandedModal";
import {MenuItem} from "@material-ui/core";
import AdditionalSkills, {Label} from "../../../../components/AdditionalSkills/AdditionalSkills";
import {MaterialSelector} from "../../../../components/Selector";
import { HygienistSkill, roles } from "utils/constants";
import {HYGIENIST_TYPES, HygienistProps, SkillsProps} from "../../../../utils/types";
import {getRoleByTypeNumber, getTypeByRoleNumber} from "../../../../utils";

import {RadioInput, RadioInputGroup} from "../../../../components/Inputs/RadioInput";
import {CheckboxInput} from "../../../../components/Inputs/CheckboxInput";

interface QualificationProps {
    hygienist: {
        skills: HygienistSkill[],
    } & HygienistProps,
    isOpen: boolean,
    onClose: () => void,
}

export const Qualification: FC<QualificationProps> = (
    {
        hygienist,
        isOpen,
        onClose,
    }
) => {
    const [loading, setLoading] = useState<boolean>(false);

    const hygienistLicenseQualification = !!hygienist?.skills.find(skill => skill.license && skill.qualification)

    const province = hygienist?.province

    const roleNumber = getRoleByTypeNumber(hygienist?.type);
    const isHygienistOrAssistant = roleNumber === roles.HYGIENIST || roleNumber === roles.ASSISTANT
    const submitRef = useRef<HTMLInputElement>(null)
    const [skills, setSkills] = useState<HygienistSkill[]>([]);

    const [userQualification, setUserQualification] = useState<string | undefined>('');
    const [checkedSkills, setCheckedSkills] = useState<HygienistSkill[]>(hygienist.skills);
    const [licensed, setLicensed] = useState<boolean | undefined>(isHygienistOrAssistant ? !hygienistLicenseQualification : true);
    const [isLeftHanded, setIsLeftHanded] = useState<boolean>(!!hygienist?.isLeftHanded);
    const [isLeftHandedModal, setIsLeftHandedModal] = useState<boolean>(false);

    useEffect(() => {
        if (isHygienistOrAssistant) {
            getSkills();
        }
    },[roleNumber, province])

    useEffect(() => {
        if (isOpen) {
            document.querySelector( '#bodySignUp' )?.scrollIntoView( { block: 'start' } )
        }
    },[isOpen])

    useEffect(() => {
        if (isOpen && skills.length > 0) {
            const qualificationSkills = skills.filter((skill: HygienistSkill) => skill.qualification && !skill.license);            
            const userExistingQualification = hygienist.skills.find(userSkill => 
                qualificationSkills.some(qualSkill => qualSkill.id === userSkill.id)
            );
            
            if (userExistingQualification) {
                const matchingSkill = skills.find(skill => skill.id === userExistingQualification.id);
                if (matchingSkill) {
                    setUserQualification(matchingSkill.name);
                }
            } else {
                setUserQualification(undefined);
            }
        }
    }, [isOpen, skills, hygienist.skills]);

    useEffect(() => {
        if (licensed === false) {
            setUserQualification('');
        }
    }, [licensed]);

    const getSkills = async () => {
        const skills = await Api.getSkills({type: getTypeByRoleNumber(roleNumber), state: province});
        setSkills(skills);
    }

    const [errorMessage, setErrorMessage] = useState<any>({
        license: '',
        education: '',
        graduationYear: '',
        about: '',
    });

    let type = undefined;
    let label = '';
    let labelDescription = '';
    let noLicenseLabel = '';
    let noLicenselabelDescription = '';

    if (isHygienistOrAssistant) {
        type = roleNumber === roles.HYGIENIST ? HYGIENIST_TYPES.HYGIENIST : HYGIENIST_TYPES.ASSISTANT;
    }
    const licenseSkill = skills.find(skill => skill.license && !skill.qualification)
    const qualificationSkills = skills.filter((skill: HygienistSkill) => skill.qualification && !skill.license);
    const licenseQualificationSkill = skills.find(skill => skill.license && skill.qualification)
    if(licenseSkill) {
        label = licenseSkill.name === 'RDH'
            ? 'I am an Actively Licensed Registered Dental Hygienist (RDH)'
            : 'I am a Certified or Registered Dental Assistant';
        labelDescription = licenseSkill.name === 'RDH'
            ? `This means you are formally trained, qualified and have an active RDH license in good standing with your regulatory body and can legally work as a Dental Hygienist in ${province}.`
            : `This means you have received formal training as a Dental Assistant and have an active Certification or Registration in good standing in ${province}.`;
    }
    if (licenseQualificationSkill) {
        noLicenseLabel = `I am not a Certified or Registered Dental Assistant`
        noLicenselabelDescription = `The means that you have been trained on-the-job to a high level of competency and professionalism, and are skilled and qualified to work as a dental assistant in ${province}`
    }

    const onSubmitSkills = async (event: any) => {
        event.preventDefault()
        let errors = {}
        setErrorMessage(errors as any);
        const {qualification} = event.target
        const {isLeftHanded} = event.target
        const checkedSkills = skills.filter((skill: HygienistSkill) => event.target[skill.name]?.checked)

        if(licensed || !licenseQualificationSkill) {
            if (!licensed && roles.HYGIENIST === roleNumber) {
                errors = {...errors, licensed: 'Please confirm your qualification'};
            }
        }
        if(licensed === undefined) {
            errors = {...errors, licensed: 'Please confirm your qualification'};
        }
        
        if (licensed && roleNumber === roles.ASSISTANT) {
            if (!userQualification) {
                errors = {...errors, qualification: 'Please select your qualification level'};
            }
        }

        if(Object.values(errors).find((err: any) => !!err.length)) {
            setErrorMessage(errors as any);
            document.querySelector( '#bodySignUp' )?.scrollIntoView( { behavior: 'smooth', block: 'start' } );
            return console.log('ERROR', errors)
        }

        if (isHygienistOrAssistant) {
            setLoading(true);
            try {
                // First update the local state
                setCheckedSkills(checkedSkills);

                // Prepare the skills array
                const checkedSkillIds = checkedSkills.map((skill: HygienistSkill) => skill.id)
                const qualificationSkill = qualification?.value
                    ? skills.find((skill: HygienistSkill) => skill.name === qualification?.value)
                    : undefined;

                // If we have a qualification skill and user is licensed, add it
                const resultSkills = qualificationSkill && licensed
                    ? [...checkedSkillIds, Number(qualificationSkill?.id)]
                    : checkedSkillIds;

                // Remove duplicates and send to API
                const arraySkillIds = Array.from(new Set(resultSkills))
                await Api.replaceHygienistSkills(arraySkillIds);

                if (hygienist.type === 0 && !!hygienist?.isLeftHanded !== isLeftHanded.checked) {
                    await Api.updateHygienistAccount({
                        isLeftHanded: isLeftHanded.checked ? 1 : 0,
                    });
                }

            } catch (error) {
                console.error('Error updating skills:', error);
                // Revert local state on error
                setCheckedSkills(hygienist.skills);
            } finally {
                setLoading(false);
            }
        }
        return onClose()
    }

    const graduateYears = [];
    for (let i = new Date().getFullYear(); i >= 1960; i--) {
        graduateYears.push(i);
    }


    const qualificationHandler = (e: any) => {
        setUserQualification(e.target.value)
        if(e.target.value) {
            setErrorMessage({...errorMessage, qualification: ''})
        }
    }

    return (
        <>
            <FullscreenModal
                title={'Qualification & Skills'}
                isOpen={isOpen}
                buttonText={'Save'}
                onClose={onClose}
                onSubmit={onSubmitSkills}
                stickyBottom={true}
                submitRef={submitRef}
            >
                <SimpleModal isOpen={isLeftHandedModal}>
                    <LeftHandedModal onCloseButtonClick={() => setIsLeftHandedModal(false)}/>
                </SimpleModal>

                {licenseQualificationSkill ?
                    <div className="list-block inset inputBox">
                        <RadioInputGroup
                            name={licenseSkill?.name || ''}
                            value={!!licensed}
                            onChange={(e) => {
                                setLicensed(e.target.value === 'true')
                            }}
                        >
                            <RadioInput
                                name={licenseSkill?.name || ''}
                                value={true}
                                label={<Label
                                    style={{color: errorMessage?.licensed ? '#f44336' : ''}}
                                    label={label}
                                    description={labelDescription}
                                />}
                            />
                            <RadioInput
                                value={false}
                                name={licenseQualificationSkill.name}
                                label={<Label
                                    label={noLicenseLabel}
                                    description={noLicenselabelDescription}
                                />}
                            />
                        </RadioInputGroup>
                    </div>
                    : <div className="list-block inset inputBox">
                        <CheckboxInput
                            name={licenseSkill?.name || ''}
                            label={<Label
                                style={{color: errorMessage?.licensed ? '#f44336' : ''}}
                                label={label}
                                description={labelDescription}
                            />}
                            onChange={(e) => {
                                setLicensed(true)
                            }}
                            checked={licensed === true}
                            disabled={true}
                        />
                    </div>
                }

                {!!qualificationSkills.length && (() => {
                    return true;
                })() &&
                    <div className="list-block inset inputBox">
                        <MaterialSelector
                            key={`qualification-${userQualification || 'empty'}`}
                            disabled={licensed === false}
                            id={'qualification'}
                            name={'qualification'}
                            label={'Qualification'}
                            errorMessage={errorMessage?.qualification}
                            onChange={qualificationHandler}
                            value={userQualification}
                        >
                            {
                                qualificationSkills.map((skill: HygienistSkill) => {
                                    return (<MenuItem key={skill.id} value={skill.name} id={skill.name}>{skill.label}</MenuItem>)
                                })
                            }
                        </MaterialSelector>
                    </div>}

                {roleNumber === roles.HYGIENIST &&
                    <div className="isLeftHandedContainer">
                        <CheckboxInput
                            checked={isLeftHanded}
                            name={"isLeftHanded"}
                            onChange={(e) => {
                                setIsLeftHanded(e.target.checked)
                            }}
                            label={<Label
                                style={{color: errorMessage?.licensed ? '#f44336' : ''}}
                                label={"I'm left-handed"}
                                description={""}
                            />}
                        />

                        <InfoOutlinedIcon className="infoIcon" onClick={() => setIsLeftHandedModal(true)}/>
                    </div>
                }

                {typeof type === 'number' ? <AdditionalSkills
                    disabled={false}
                    skills={skills}
                    checkedSkills={checkedSkills}
                    state={province || ''}
                    qualificationSkill={qualificationSkills.find(skill => skill.name === userQualification)}

                /> : ''}
            </FullscreenModal>
            <FixedLoader
                loading={loading}
            />
        </>
    );
}